import React, { useEffect, useContext, useState, useMemo, useCallback } from 'react';
import {
  Box, CircularProgress, Snackbar, Tab, Tabs,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useMediaQuery } from 'react-responsive';

import useYext from '../yext/hooks/useYext';
import LocationSelector from '../yext/components/LocationSelector';
import ScanResults from '../yext/components/ScanResults';
import NoLocations from '../yext/components/NoLocations';
import yextStyles from '../yext/Yext.style';
import MobileTitle from '../../customUI/MobileTitle';
import { storeContext } from '../provider/Provider';
import { yextActions } from '../../store/yext-reducer';
import ReviewsTab from './components/ReviewsTab';
import {
  reviewRemovalGoogleReviewsList,
  reviewRemovalRequestsList,
  reviewRemovalRequestsCreate,
  normalizeReview,
  summarizeReviews,
  indexRemovalRequests,
} from './services/reputation.service';

const useStyles = makeStyles((theme) => ({
  tabs: {
    marginTop: 12,
    borderBottom: '1px solid #E4E7EB',
    '& .MuiTab-root': {
      textTransform: 'none',
      fontSize: 14,
      fontWeight: 600,
      minWidth: 'auto',
      padding: '8px 20px',
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#00B5E2',
    },
  },
  tabBadge: {
    marginLeft: 8,
    background: '#B26A00',
    color: 'white',
    borderRadius: 999,
    padding: '1px 7px',
    fontSize: 11,
    fontWeight: 700,
  },
  tabPanel: {
    paddingTop: 16,
  },
  reviewsSection: {
    background: '#F8F9FB',
    border: '1px solid #E4E7EB',
    borderRadius: 12,
    padding: 20,
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  listingsSection: {
    paddingTop: 60,
  },
  splitView: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  splitColumn: {
    minWidth: 0,
  },
  locationHeader: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1A2332',
    marginBottom: 4,
  },
  locationSubhead: {
    fontSize: 13,
    color: '#6C7480',
    marginBottom: 16,
  },
}));

const ReputationWrapper = () => {
  const yextClasses = yextStyles();
  const classes = useStyles();
  const isPhone = useMediaQuery({ query: '(max-width: 768px)' });
  const isWide = useMediaQuery({ query: '(min-width: 1280px)' });
  const { state, dispatch } = useContext(storeContext);
  const {
    snackbarOpen,
    snackbarMessage,
    isFetchingLocations,
    error,
    loadLocations,
    closeSnackbar,
    currentLocation,
    locations,
  } = useYext();

  const [activeTab, setActiveTab] = useState(0);
  const [removalRequests, setRemovalRequests] = useState({});
  const [removalSnackbarOpen, setRemovalSnackbarOpen] = useState(false);
  const [reviewsPayload, setReviewsPayload] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsLoadingMore, setReviewsLoadingMore] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [sortBy, setSortBy] = useState('newestFirst');

  useEffect(() => {
    dispatch({
      type: yextActions.SET_CURRENT_LOCATION,
      payload: { currentLocation: null },
    });
    dispatch({
      type: yextActions.SET_SCAN_RESULTS,
      payload: { scanResults: null },
    });
    loadLocations();
  }, [state.websiteSwitch?.currentWebsite, dispatch, loadLocations]);

  useEffect(() => {
    dispatch({
      type: yextActions.SET_SCAN_RESULTS,
      payload: { scanResults: null },
    });
    setActiveTab(0);
  }, [currentLocation?.id, dispatch]);

  // Fetch first page of Google reviews + existing removal requests.
  useEffect(() => {
    if (!currentLocation) {
      setReviewsPayload(null);
      setRemovalRequests({});
      setNextPageToken(null);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setReviewsLoading(true);
      setReviewsError(null);
      setNextPageToken(null);
      try {
        const [reviewsResp, requestsResp] = await Promise.all([
          reviewRemovalGoogleReviewsList({ nap_listing: currentLocation.id, sort_by: sortBy }),
          reviewRemovalRequestsList({ nap_listing: currentLocation.id }),
        ]);
        const reviewsData = reviewsResp.data;
        const requestsData = requestsResp.data;

        if (cancelled) return;

        const normalized = (reviewsData.reviews || []).map(normalizeReview);

        setReviewsPayload({
          id: currentLocation.id,
          label: currentLocation.business_name || currentLocation.name || 'Location',
          name: currentLocation.business_name || currentLocation.name || 'Location',
          address: currentLocation.address || '',
          phone: currentLocation.phone || '',
          reviews: normalized,
          reviewSummary: {
            ...summarizeReviews(normalized),
            // Use the real total from Google, not just the loaded count
            totalFromGoogle: reviewsData.total_reviews || normalized.length,
            ratingFromGoogle: reviewsData.rating,
          },
        });
        setNextPageToken(reviewsData.next_page_token || null);
        setRemovalRequests(indexRemovalRequests(requestsData));
      } catch (err) {
        if (!cancelled) {
          setReviewsError(
            err?.response?.data?.error || 'Unable to load reviews for this location.',
          );
          setReviewsPayload(null);
        }
      } finally {
        if (!cancelled) setReviewsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [currentLocation, sortBy]); // eslint-disable-line

  const handleLoadMore = useCallback(async () => {
    if (!nextPageToken || !currentLocation || reviewsLoadingMore) return;
    setReviewsLoadingMore(true);
    try {
      const resp = await reviewRemovalGoogleReviewsList({
        nap_listing: currentLocation.id,
        sort_by: sortBy,
        next_page_token: nextPageToken,
      });
      const { data: pageData } = resp;
      const newReviews = (pageData.reviews || []).map(normalizeReview);

      setReviewsPayload((prev) => {
        if (!prev) return prev;
        const allReviews = [...prev.reviews, ...newReviews];
        return {
          ...prev,
          reviews: allReviews,
          reviewSummary: {
            ...summarizeReviews(allReviews),
            totalFromGoogle: prev.reviewSummary.totalFromGoogle,
            ratingFromGoogle: prev.reviewSummary.ratingFromGoogle,
          },
        };
      });
      setNextPageToken(pageData.next_page_token || null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load more reviews:', err);
    } finally {
      setReviewsLoadingMore(false);
    }
  }, [nextPageToken, currentLocation, reviewsLoadingMore]);

  const removalRequestsForCurrent = useMemo(() => {
    if (!reviewsPayload) return {};
    const ids = new Set(reviewsPayload.reviews.map((r) => r.id));
    return Object.fromEntries(
      Object.entries(removalRequests).filter(([id]) => ids.has(id)),
    );
  }, [reviewsPayload, removalRequests]);

  const handleRequestRemoval = useCallback(async (reviewId, payload) => {
    try {
      const resp = await reviewRemovalRequestsCreate(payload);
      const created = resp.data;
      setRemovalRequests((prev) => ({
        ...prev,
        [payload.review_google_id]: {
          id: created.id,
          reason: created.reason,
          notes: created.notes,
          requestedAt: created.added,
          status: created.status,
          reviewAuthor: payload.review_author,
          reviewRating: payload.review_rating,
          reviewBody: payload.review_body,
        },
      }));
      setRemovalSnackbarOpen(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to create removal request:', err);
    }
  }, []);

  if (isFetchingLocations) {
    return (
      <div className={isPhone ? '' : yextClasses.wrapper}>
        <div className={isPhone ? '' : yextClasses.card}>
          <Box className={yextClasses.loadingContainer}>
            <CircularProgress />
          </Box>
        </div>
      </div>
    );
  }

  if (!locations || locations.length === 0) {
    return <NoLocations />;
  }

  return (
    <div className={isPhone ? '' : yextClasses.wrapper}>
      <div className={isPhone ? '' : yextClasses.card}>
        <div className={isPhone ? '' : yextClasses.card__wrapper}>
          {isPhone ? (
            <MobileTitle title="Reputation" />
          ) : (
            <div className={yextClasses.heading}>Reputation</div>
          )}

          <Box className={yextClasses.contentSection}>
            <LocationSelector />

            {currentLocation && (() => {
              const reviewsBlock = (
                <>
                  {reviewsLoading && (
                    <div className={classes.reviewsSection}>
                      <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={28} />
                      </Box>
                    </div>
                  )}
                  {!reviewsLoading && reviewsError && (
                    <div className={classes.reviewsSection}>
                      <Alert severity="info" style={{ borderRadius: 10 }}>
                        {reviewsError}
                      </Alert>
                    </div>
                  )}
                  {!reviewsLoading && !reviewsError && reviewsPayload && (
                    <div className={classes.reviewsSection}>
                      <div className={classes.locationHeader}>
                        {reviewsPayload.name}
                      </div>
                      <div className={classes.locationSubhead}>
                        {reviewsPayload.address}
                      </div>
                      <ReviewsTab
                        location={reviewsPayload}
                        removalRequests={removalRequestsForCurrent}
                        onRequestRemoval={handleRequestRemoval}
                        hasMore={Boolean(nextPageToken)}
                        loadingMore={reviewsLoadingMore}
                        onLoadMore={handleLoadMore}
                        sortBy={sortBy}
                        onSortChange={setSortBy}
                      />
                    </div>
                  )}
                </>
              );

              if (isWide) {
                return (
                  <div className={classes.tabPanel}>
                    <div className={classes.splitView}>
                      <div className={classes.splitColumn}>
                        <div className={classes.listingsSection}>
                          <ScanResults />
                        </div>
                      </div>
                      <div className={classes.splitColumn}>
                        {reviewsBlock}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <>
                  <Tabs
                    className={classes.tabs}
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    indicatorColor="primary"
                  >
                    <Tab label="PowerListings Scan Results" />
                    <Tab label={
                      <span>
                        Reviews
                        {Object.keys(removalRequests).length > 0 && (
                          <span className={classes.tabBadge}>
                            {Object.keys(removalRequests).length}
                          </span>
                        )}
                      </span>
                    } />
                  </Tabs>

                  <div className={classes.tabPanel}>
                    {activeTab === 0 && (
                      <div className={classes.listingsSection}>
                        <ScanResults />
                      </div>
                    )}
                    {activeTab === 1 && reviewsBlock}
                  </div>
                </>
              );
            })()}
          </Box>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={closeSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={closeSnackbar} severity={error ? 'error' : 'success'}>
              {snackbarMessage}
            </Alert>
          </Snackbar>

          <Snackbar
            open={removalSnackbarOpen}
            autoHideDuration={4000}
            onClose={() => setRemovalSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setRemovalSnackbarOpen(false)}
              severity="success"
              variant="filled"
            >
              Removal request sent to your Hennessey reputation specialist.
            </Alert>
          </Snackbar>
        </div>
      </div>
    </div>
  );
};

export default ReputationWrapper;
